// Motion helpers for the public pages. Rules kept here in one place:
// - the home page moves only its hero stage (app/art/vault-chip/HANDOFF.md
//   M1): no scroll reveals anywhere;
// - the app's entrance (/welcome, /locked, /create, /restore) moves nothing:
//   its one picture is a still (components/EntranceArt.tsx), so it uses
//   none of these hooks;
// - reduced motion turns every effect off and leaves content in place;
// - the visitor's Pause motion choice is remembered per browser;
// - every listener is removed on unmount.

import { useEffect, useState } from 'react';

const REDUCED = '(prefers-reduced-motion: reduce)';

function mediaMatches(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => mediaMatches(query));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery(REDUCED);
}

/** Land on the URL's #section once the page has rendered (the browser's own jump ran before it existed). */
export function useHashLanding(): void {
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, []);
}

const AMBIENT_KEY = 'thevault.site.ambient';

/** The visitor's Pause motion choice for the home hero (persisted; per-browser convenience only). */
export function useAmbientPreference(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return window.localStorage.getItem(AMBIENT_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const set = (value: boolean) => {
    setOn(value);
    try {
      window.localStorage.setItem(AMBIENT_KEY, value ? 'on' : 'off');
    } catch {
      /* storage unavailable: the choice lasts for this page view */
    }
  };
  return [on, set];
}
